import React, {FC, useCallback, useMemo} from 'react';
import {useGlobals, withGlobals} from '../../../../../../../globalPropsContext';
import {Dropdown, DropdownProps} from 'wix-ui-tpa/cssVars';
import {IProduct, IProductOption} from '../../../../../../../types/galleryTypes';
import {useOptionSelectionLogic} from '../../../../hooks/options/useOptionSelectionLogic';
import {ProductOptionType} from '@wix/wixstores-graphql-schema/dist/es/src/graphql-schema';
import {classes as dropdownStylableClasses} from './ProductQuickAddDropdown.st.css';
import s from './ProductQuickAddDropdown.scss';
import classNames from 'classnames';
import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';

export enum ProductQuickAddDropdownDataHook {
  Root = 'ProductQuickAddDropdownDataHook.Root',
}
export interface IProductQuickAddDropdownProps extends IProvidedTranslationProps {
  productId: IProduct['id'];
  option: IProductOption;
}

const ProductQuickAddDropdownComp: FC<IProductQuickAddDropdownProps> = ({productId, option, t}) => {
  const {productsVariantInfoMap, handleProductsOptionsChange, shouldShowMobile, host, styles, stylesParams} =
    useGlobals();
  const selectionIds = productsVariantInfoMap?.[productId]?.variantSelectionIds;
  const selectionsAvailability = productsVariantInfoMap?.[productId]?.selectionsAvailability[option.id];

  const {visibleSelections, calculateNextSelectionIds, selectedSelectionId, isSelectionDisabled} =
    useOptionSelectionLogic({
      option,
      selectionIds,
      selectionsAvailability,
    });

  const handleDropdownChange: DropdownProps['onChange'] = useCallback(
    (selectedOption) => {
      const pickedSelectionId = parseInt(selectedOption.id, 10);
      const nextSelectionIds = calculateNextSelectionIds(pickedSelectionId);

      void handleProductsOptionsChange({
        productId,
        selectionIds: nextSelectionIds,
        optionType: ProductOptionType.DROP_DOWN,
      });
    },
    [calculateNextSelectionIds, handleProductsOptionsChange, productId]
  );

  const showLabel = styles.get(stylesParams.gallery_quickAddShowProductOptionsTitles);

  /* istanbul ignore next */
  const appendTo = useMemo(() => {
    const siteContainer = document.getElementById('SITE_CONTAINER');
    return typeof jest !== 'undefined' ? undefined : siteContainer || document.body;
  }, []);

  return (
    <div className={classNames(s.root, {[s.isMobile]: shouldShowMobile})}>
      <Dropdown
        data-hook={ProductQuickAddDropdownDataHook.Root}
        optionsContainerId={ProductQuickAddDropdownDataHook.Root}
        appendTo={appendTo}
        className={classNames(dropdownStylableClasses.dropdown, host.id)}
        label={showLabel ? option.title : undefined}
        placeholder={showLabel ? t('quickAddDropdownSelect') : option.title}
        aria-label={option.title}
        aria-required
        showAsterisk
        selectedId={selectedSelectionId || 'no selection'}
        options={visibleSelections?.map((selection) => ({
          id: selection.id.toString(),
          isSelectable: !isSelectionDisabled(selection.id),
          value: selection.description,
        }))}
        onChange={handleDropdownChange}
        mobileNativeSelect={shouldShowMobile}
        upgrade
        fixed
        flip
      />
    </div>
  );
};

export const ProductQuickAddDropdown = withGlobals(withTranslations()(ProductQuickAddDropdownComp));
